import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Send, 
  Paperclip, 
  Mic, 
  Phone, 
  Video, 
  User, 
  X,
  MoreVertical,
  Image,
  FileText,
  Download,
  Smile,
  MapPin,
  Music,
  CheckCircle,
  Trash2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { chatService } from '../services/chatService';
import { isConsultationCompleted, endConsultation } from '../services/consultationService';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import MediaPreviewModal from './MediaPreviewModal';
import VoiceRecorder from './VoiceRecorder';
import MessageBubble from './MessageBubble';
import { validateFileForUpload, formatFileSize } from '../utils/fileUtils';

// Utility function to safely convert Firebase Timestamps to readable strings
const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Not specified';
  
  if (typeof timestamp === 'object' && timestamp.toDate) {
    // Firebase Timestamp object
    return timestamp.toDate().toLocaleDateString('en-IN');
  } else if (typeof timestamp === 'string') {
    // Already a string
    return timestamp;
  } else if (timestamp instanceof Date) {
    // JavaScript Date object
    return timestamp.toLocaleDateString('en-IN');
  } else {
    // Fallback
    return timestamp.toString();
  }
};

const LawyerChatConversation = () => {
  const { caseId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { caseData, clientId, chatData } = location.state || {};
  const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  
  // State management
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [clientInfo, setClientInfo] = useState(null);
  const [clientProfile, setClientProfile] = useState(null);
  const [showClientProfile, setShowClientProfile] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [showMediaPreview, setShowMediaPreview] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [typingStatus, setTypingStatus] = useState({});
  const [actualChatId, setActualChatId] = useState(null);
  const [consultationCompleted, setConsultationCompleted] = useState(false);

  // Fetch client profile from users collection
  const fetchClientProfile = async (clientId) => {
    try {
      console.log('🔍 Fetching client profile for ID:', clientId);
      const userRef = doc(db, 'users', clientId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        console.log('✅ Client profile fetched:', userData);
        
        // Try multiple field names for client name
        const fullName = userData.full_name || 
                        userData.fullName || 
                        userData.displayName || 
                        userData.name || 
                        (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : null) ||
                        'Unknown Client';
        
        // Try multiple field names for profile picture
        const profilePic = userData.profile_picture || 
                          userData.profilePic || 
                          userData.photoURL || 
                          userData.avatar || 
                          userData.photo || 
                          '/default-avatar.svg';
        
        const clientData = {
          id: clientId,
          fullName: fullName,
          profilePic: profilePic,
          email: userData.email || 'Not available',
          phone: userData.phone || userData.phoneNumber || 'Not available'
        };
        
        console.log('✅ fetchClientProfile returning:', clientData);
        return clientData;
      } else {
        console.log('❌ Client profile not found for ID:', clientId);
        // Return fallback data
        return {
          id: clientId,
          fullName: 'Unknown Client',
          profilePic: '/default-avatar.svg',
          email: 'Not available',
          phone: 'Not available'
        };
      }
    } catch (error) {
      console.error('❌ Error fetching client profile:', error);
      // Return fallback data on error
      return {
        id: clientId,
        fullName: 'Unknown Client',
        profilePic: '/default-avatar.svg',
        email: 'Not available',
        phone: 'Not available'
      };
    }
  };

  // Load chat data with fresh user profile
  useEffect(() => {
    console.log('🚀 Component mounted with caseId:', caseId);
    console.log('🚀 Current location:', location.pathname);
    console.log('🚀 Location state:', location.state);
    
    const loadChatByCaseId = async (caseId) => {
      try {
        setLoading(true);
        console.log('🔍 Finding chat for case:', caseId);
        
        let existingChat = null;
        let clientIdToUse = null;
        
        // Determine which client ID to use for search
        if (caseData?.clientId) {
          clientIdToUse = caseData.clientId;
        } else if (chatData?.clientId) {
          clientIdToUse = chatData.clientId;
        } else if (caseId && caseId.length > 20) {
          // Treat caseId as client ID if it's long enough
          clientIdToUse = caseId;
        }
        
        // If we have a client ID, search for existing chat
        if (clientIdToUse && user?.uid) {
          console.log('🔍 Searching for existing chat with clientId:', clientIdToUse);
          
          // Direct Firestore query instead of dynamic import
          const { collection, query, where, getDocs } = await import('firebase/firestore');
          const { db } = await import('../config/firebase');
          
          const chatsRef = collection(db, 'chats');
          const existingChatQuery = query(
            chatsRef,
            where('clientId', '==', clientIdToUse),
            where('lawyerId', '==', user.uid)
          );
          
          const existingChatSnapshot = await getDocs(existingChatQuery);
          
          if (!existingChatSnapshot.empty) {
            const chatDoc = existingChatSnapshot.docs[0];
            existingChat = { id: chatDoc.id, ...chatDoc.data() };
            console.log('✅ Found existing chat:', existingChat.id);
          } else {
            console.log('📭 No existing chat found for client:', clientIdToUse);
          }
        }
        
        // Fallback: try to find by caseId if no existing chat found
        if (!existingChat) {
          console.log('🔄 Fallback: searching by caseId');
          const { collection, query, where, getDocs } = await import('firebase/firestore');
          const { db } = await import('../config/firebase');
          
          const chatsRef = collection(db, 'chats');
          const caseIdQuery = query(
            chatsRef,
            where('caseId', '==', caseId)
          );
          const chatSnapshot = await getDocs(caseIdQuery);
          
          if (!chatSnapshot.empty) {
            const chatDoc = chatSnapshot.docs[0];
            existingChat = { id: chatDoc.id, ...chatDoc.data() };
            console.log('✅ Found chat by caseId:', existingChat.id);
          }
        }
        
        if (existingChat) {
          const actualChatId = existingChat.id;
          console.log('✅ Using existing chat:', actualChatId);
          
          // Store the actual chatId for real-time messaging
          setActualChatId(actualChatId);
          
          // Load client data FIRST - FIXED: Load client profile before messages
          const clientId = existingChat.clientId;
          if (clientId) {
            console.log('🔍 Loading client profile FIRST for:', clientId);
            await loadClientProfile(clientId);
          } else {
            console.log('❌ No clientId found in existing chat');
          }
          
          // Then load messages for this chat
          await loadChatMessages(actualChatId);
          
          // Check consultation status
          const isCompleted = existingChat.status === 'completed';
          setConsultationCompleted(isCompleted);
          console.log('🔍 Consultation status:', existingChat.status, 'Is completed:', isCompleted);
          
        } else {
          console.log('🆕 No chat found, attempting to create new one...');
          
          // Only create new chat if we have valid client info
          let shouldCreateChat = false;
          let clientIdToUse = null;
          
          if (caseData?.clientId) {
            clientIdToUse = caseData.clientId;
            shouldCreateChat = true;
            console.log('✅ Will create chat using caseData.clientId:', clientIdToUse);
          } else if (chatData?.clientId) {
            clientIdToUse = chatData.clientId;
            shouldCreateChat = true;
            console.log('✅ Will create chat using chatData.clientId:', clientIdToUse);
          } else if (clientInfo?.id) {
            clientIdToUse = clientInfo.id;
            shouldCreateChat = true;
            console.log('✅ Will create chat using clientInfo.id:', clientIdToUse);
          } else if (caseId && caseId.length > 20) {
            // Try to treat caseId as a client ID if it's long enough
            console.log('🔄 Trying to treat caseId as client ID for chat creation');
            try {
              const freshClientData = await fetchClientProfile(caseId);
              if (freshClientData && freshClientData.id) {
                console.log('✅ Client data loaded from caseId:', freshClientData);
                setClientInfo(freshClientData);
                setClientProfile(freshClientData);
                clientIdToUse = freshClientData.id;
                shouldCreateChat = true;
              } else {
                throw new Error('Invalid client data');
              }
            } catch (error) {
              console.error('❌ Failed to load client data from caseId:', error);
              setLoading(false);
              toast.error('Client information not available. Please try again.');
            }
          }
          
          if (shouldCreateChat && clientIdToUse) {
            // Load client info if not already loaded
            if (!clientInfo) {
              const freshClientData = await fetchClientProfile(clientIdToUse);
              setClientInfo(freshClientData);
              setClientProfile(freshClientData);
            }
            
            // Create the chat
            await createNewChat(caseId);
          } else {
            // No client info available, show error with debug info
            console.error('❌ No client info available to create chat');
            console.error('❌ Debug info:', {
              caseData: caseData,
              chatData: chatData,
              clientId: clientId,
              clientInfo: clientInfo,
              caseId: caseId
            });
            setLoading(false);
            toast.error('Client information not available. Please try again.');
          }
        }
        
        setLoading(false);
        
      } catch (error) {
        console.error('❌ Error loading chat by caseId:', error);
        setLoading(false);
      }
    };

    if (caseId) {
      console.log('🔄 Loading chat for case:', caseId);
      console.log('🔄 Case data from location state:', caseData);
      console.log('🔄 Client ID from location state:', clientId);
      
      // If we have caseData but no clientId, try to load client info first
      if (caseData?.clientId && !clientId) {
        console.log('🔄 Loading client info from caseData:', caseData.clientId);
        fetchClientProfile(caseData.clientId).then(freshClientData => {
          setClientInfo(freshClientData);
          setClientProfile(freshClientData);
        });
      }
      
      // If we have chatData but no caseData, try to extract client info from chatData
      if (chatData && !caseData && !clientId) {
        console.log('🔄 Loading client info from chatData:', chatData);
        if (chatData.clientId) {
          fetchClientProfile(chatData.clientId).then(freshClientData => {
            setClientInfo(freshClientData);
            setClientProfile(freshClientData);
          });
        } else if (chatData.client && chatData.client.id) {
          fetchClientProfile(chatData.client.id).then(freshClientData => {
            setClientInfo(freshClientData);
            setClientProfile(freshClientData);
          });
        }
      }
      
      // Also try to load client info from the caseId itself if it looks like a user ID
      if (!caseData?.clientId && !clientId && !chatData && caseId.length > 20) {
        console.log('🔄 CaseId looks like a user ID, trying to load client profile directly');
        fetchClientProfile(caseId).then(freshClientData => {
          setClientInfo(freshClientData);
          setClientProfile(freshClientData);
        });
      }
      
      loadChatByCaseId(caseId);
    } else {
      console.log('❌ No caseId provided, cannot load chat');
      setLoading(false);
    }
  }, [caseId]);

  // Load chat messages
  const loadChatMessages = async (actualChatId) => {
    try {
      console.log('📨 Loading messages for chat:', actualChatId);
      
      // Try to load messages directly as a fallback
      const { collection, getDocs } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      
      const messagesRef = collection(db, 'chats', actualChatId, 'messages');
      const messagesSnapshot = await getDocs(messagesRef);
      
      if (!messagesSnapshot.empty) {
        const messages = messagesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        // Sort messages by createdAt
        messages.sort((a, b) => {
          const timeA = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
          const timeB = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
          return timeA - timeB;
        });
        
        console.log('📨 Fallback: Loaded', messages.length, 'messages directly');
        setMessages(messages);
      } else {
        console.log('📭 No messages found in fallback load');
        setMessages([]);
      }
    } catch (error) {
      console.error('❌ Error loading chat messages:', error);
      setMessages([]);
    }
  };

  // Create new chat if none exists
  const createNewChat = async (caseId) => {
    try {
      const { collection, addDoc, serverTimestamp, doc, updateDoc, query, where, getDocs } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      
      // Get clientId from multiple sources with fallbacks
      const clientIdToUse = clientInfo?.id || caseData?.clientId || chatData?.clientId || clientId;
      
      console.log('🔍 Creating chat with clientId:', clientIdToUse);
      console.log('🔍 Available sources:', { 
        clientInfoId: clientInfo?.id, 
        caseDataClientId: caseData?.clientId, 
        chatDataClientId: chatData?.clientId, 
        componentClientId: clientId 
      });
      
      if (!clientIdToUse) {
        throw new Error('No client ID available to create chat');
      }
      
      // Check if chat already exists for this client-lawyer pair
      const chatsRef = collection(db, 'chats');
      const existingChatQuery = query(
        chatsRef,
        where('clientId', '==', clientIdToUse),
        where('lawyerId', '==', user.uid)
      );
      
      const existingChatSnapshot = await getDocs(existingChatQuery);
      
      if (!existingChatSnapshot.empty) {
        // Chat exists, update it with new case info
        const existingChatDoc = existingChatSnapshot.docs[0];
        const existingChatId = existingChatDoc.id;
        
        console.log('✅ Existing chat found, updating with new case info:', existingChatId);
        
        await updateDoc(doc(db, 'chats', existingChatId), {
          caseId: caseId,
          bookingId: caseId,
          bookingDate: new Date().toISOString(),
          lastBookingTime: serverTimestamp(),
          updatedAt: serverTimestamp(),
          // Update client profile data if newer
          clientName: clientInfo?.fullName || caseData?.clientName || existingChatDoc.data().clientName || 'Client',
          clientPhoto: clientInfo?.profilePic || caseData?.clientPhoto || existingChatDoc.data().clientPhoto || '/default-client.png',
          // Reset status to active for new booking
          status: 'active'
        });
        
        // Store the existing chatId for real-time messaging
        setActualChatId(existingChatId);
        
        // Load messages for the existing chat
        await loadChatMessages(existingChatId);
        
        console.log('✅ Existing chat updated with new case info');
        return;
      }
      
      // Create new chat with enhanced client data
      const newChatData = {
        caseId: caseId,
        clientId: clientIdToUse,
        lawyerId: user.uid,
        clientName: clientInfo?.fullName || caseData?.clientName || 'Client',
        clientPhoto: clientInfo?.profilePic || caseData?.clientPhoto || '/default-client.png',
        caseType: caseData?.caseType || 'Legal Consultation',
        status: 'active',
        bookingDate: new Date().toISOString(),
        lastBookingTime: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        messages: []
      };
      
      const chatRef = await addDoc(collection(db, 'chats'), newChatData);
      console.log('✅ New chat created:', chatRef.id);
      
      // Store the actual chatId for real-time messaging
      setActualChatId(chatRef.id);
      
      // Load messages for the new chat
      await loadChatMessages(chatRef.id);
      
    } catch (error) {
      console.error('❌ Error creating new chat:', error);
      console.error('❌ Client info available:', clientInfo);
      console.error('❌ Case data available:', caseData);
      console.error('❌ Component clientId:', clientId);
      
      // Set loading to false so user can see the error
      setLoading(false);
      
      // Show error toast
      toast.error('Failed to create chat. Please try again.');
    }
  };

  // Monitor client info changes
  useEffect(() => {
    console.log('🔄 Client info updated:', clientInfo);
    console.log('🔄 Loading state:', loading);
    if (clientInfo) {
      console.log('🔄 Client fullName in state:', clientInfo.fullName);
      console.log('🔄 Client profile pic in state:', clientInfo.profilePic);
      console.log('🔄 Client info type:', typeof clientInfo);
      console.log('🔄 Client info keys:', Object.keys(clientInfo));
    } else {
      console.log('🔄 Client info is null/undefined');
    }
  }, [clientInfo, loading]);

  // Watch for clientInfo changes and create chat if needed
  useEffect(() => {
    if (clientInfo?.id && !actualChatId && !loading) {
      console.log('🔄 Client info available, attempting to create chat');
      console.log('🔄 Client info details:', {
        id: clientInfo.id,
        fullName: clientInfo.fullName,
        profilePic: clientInfo.profilePic
      });
      createNewChat(caseId);
    }
  }, [clientInfo, actualChatId, loading, caseId]);



  // Real-time messaging subscription
  useEffect(() => {
    if (!actualChatId) return;

    console.log('🔗 Setting up real-time messaging for chat:', actualChatId);
    
    // Subscribe to real-time messages
    let unsubscribeMessages = null;
    try {
      unsubscribeMessages = chatService.subscribeToMessages(actualChatId, (realTimeMessages) => {
        console.log('📨 Real-time messages received:', realTimeMessages);
        
        if (realTimeMessages && realTimeMessages.length > 0) {
          // Only update if we have new messages (not just initial load)
          setMessages(prevMessages => {
            // Check if messages are actually different
            if (JSON.stringify(prevMessages) !== JSON.stringify(realTimeMessages)) {
              console.log('🔄 Updating messages with real-time data');
              return realTimeMessages;
            }
            return prevMessages;
          });
        } else {
          console.log('📭 No real-time messages received, keeping existing messages');
        }
      });
    } catch (error) {
      console.error('❌ Error setting up message subscription:', error);
    }

    // Subscribe to typing status
    let unsubscribeTyping = null;
    try {
      unsubscribeTyping = chatService.subscribeToTypingStatus(actualChatId, (typingData) => {
        console.log('⌨️ Real-time typing status:', typingData);
        setTypingStatus(typingData);
        
        // Check if client is typing
        const clientId = clientInfo?.id;
        if (clientId && typingData[clientId]) {
          setIsTyping(true);
          // Clear typing indicator after 3 seconds
          setTimeout(() => setIsTyping(false), 3000);
        } else {
          setIsTyping(false);
        }
      });
    } catch (error) {
      console.error('❌ Error setting up typing subscription:', error);
    }
    
    // Subscribe to chat status changes
    let unsubscribeChatStatus = null;
    try {
      unsubscribeChatStatus = chatService.subscribeToChatStatus(actualChatId, (chatData) => {
        console.log('📊 Chat status update received:', chatData);
        if (chatData && chatData.status) {
          const isCompleted = chatData.status === 'completed';
          setConsultationCompleted(isCompleted);
          console.log('🔄 Consultation status updated:', chatData.status, 'Is completed:', isCompleted);
        }
      });
    } catch (error) {
      console.error('❌ Error setting up chat status subscription:', error);
    }

    // Cleanup subscriptions on unmount
    return () => {
      console.log('🔌 Cleaning up real-time subscriptions');
      if (typeof unsubscribeMessages === 'function') {
        unsubscribeMessages();
      }
      if (typeof unsubscribeTyping === 'function') {
        unsubscribeTyping();
      }
      if (typeof unsubscribeChatStatus === 'function') {
        unsubscribeChatStatus();
      }
    };
  }, [actualChatId, clientInfo?.id]);

  

  // Handle viewing client profile
  const handleViewClientProfile = async () => {
    if (!clientInfo) return;
    
    try {
      console.log('🔍 Loading detailed client profile for:', clientInfo.id);
      
      // Fetch detailed client profile data from users collection
      let clientProfileData = null;
      if (clientInfo.id) {
        const userRef = doc(db, 'users', clientInfo.id);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          clientProfileData = userSnap.data();
          console.log('✅ Detailed client profile fetched:', clientProfileData);
        }
      }
      
      // Use caseData from location state for booking details
      let chatData = caseData || null;
      
      // Create comprehensive client profile object
      const detailedClientProfile = {
        id: clientInfo.id,
        name: clientProfileData?.full_name || clientInfo.fullName || 'Client',
        phone: clientProfileData?.phone || clientInfo.phone || 'Not available',
        city: clientProfileData?.custom_city || clientProfileData?.city || clientProfileData?.location || 'Jaipur, Rajasthan',
        bio: clientProfileData?.bio || 'hey',
        profilePic: clientProfileData?.profile_picture || clientInfo.profilePic || '/default-avatar.svg',
        email: clientProfileData?.email || clientInfo.email || 'Not available',
        
        // Booking details from chat data or default values
        bookingDetails: {
          bookingId: chatData?.id || caseId,
          status: chatData?.status || 'active',
          consultationType: chatData?.consultationType || 'general',
          caseType: chatData?.caseType || 'Legal Consultation',
          description: chatData?.description || 'No description provided by the client.',
          appointmentDate: chatData?.appointmentDate || 'Monday 1 September, 2025',
          appointmentTime: chatData?.appointmentTime || '09:06 AM',
          totalAmount: chatData?.totalAmount || chatData?.amount || 9658,
          consultationFees: chatData?.consultationFees || 8998,
          serviceCharge: chatData?.serviceCharge || 200,
          isEmergency: chatData?.isEmergency || true,
          createdAt: formatTimestamp(chatData?.createdAt) || '30/8/2025',
          lawyerAccepted: chatData?.lawyerAccepted,
          lawyerRejected: chatData?.lawyerRejected,
          consultationCompleted: chatData?.consultationCompleted
        },
        
        // Preferences
        preferences: {
          consultationMode: chatData?.consultationMode || 'general',
          preferredTime: chatData?.appointmentTime || '09:06 AM',
          language: clientProfileData?.language || 'Not specified',
          specialRequirements: 'None'
        }
      };
      
      setSelectedClient(detailedClientProfile);
      setShowClientProfile(true);
      
    } catch (error) {
      console.error('❌ Error loading detailed client profile:', error);
      toast.error('Failed to load client profile');
    }
  };

  // Load client profile
  const loadClientProfile = async (clientId) => {
    if (!clientId) {
      console.log('❌ No client ID provided');
      return;
    }

    try {
      console.log('🔍 Loading client profile for ID:', clientId);
      
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      
      const clientDoc = await getDoc(doc(db, 'users', clientId));
      
      if (clientDoc.exists()) {
        const clientData = clientDoc.data();
        console.log('✅ Client profile loaded from users collection:', clientData);
        console.log('🔍 Available fields in client data:', Object.keys(clientData));
        
        // Try multiple field names for client name - ENHANCED LOGIC
        const fullName = clientData.full_name || 
                        clientData.fullName || 
                        clientData.displayName || 
                        clientData.name || 
                        (clientData.firstName && clientData.lastName ? `${clientData.firstName} ${clientData.lastName}` : null) ||
                        'Client';
        
        // Try multiple field names for profile picture - ENHANCED LOGIC
        const profilePic = clientData.profile_picture || 
                          clientData.profilePic || 
                          clientData.photoURL || 
                          clientData.avatar || 
                          clientData.photo || 
                          '/default-avatar.svg';
        
        console.log('🔍 Field detection results:');
        console.log('  - full_name:', clientData.full_name);
        console.log('  - fullName:', clientData.fullName);
        console.log('  - displayName:', clientData.displayName);
        console.log('  - name:', clientData.name);
        console.log('  - firstName + lastName:', clientData.firstName && clientData.lastName ? `${clientData.firstName} ${clientData.lastName}` : 'N/A');
        console.log('  - Final fullName:', fullName);
        
        console.log('  - profile_picture:', clientData.profile_picture);
        console.log('  - profilePic:', clientData.profilePic);
        console.log('  - photoURL:', clientData.photoURL);
        console.log('  - avatar:', clientData.avatar);
        console.log('  - photo:', clientData.photo);
        console.log('  - Final profilePic:', profilePic);
        
        const profileData = {
          id: clientId,
          fullName: fullName,
          profilePic: profilePic,
          phone: clientData.phone || clientData.phoneNumber || 'Not available',
          email: clientData.email || 'Not available'
        };
        
        console.log('✅ Setting client info with enhanced logic:', profileData);
        setClientInfo(profileData);
        setClientProfile(profileData);
        
        // Also update the chat data with client information
        if (chatData) {
          const updatedChatData = {
            ...chatData,
            clientName: fullName,
            clientPhoto: profilePic
          };
          // Update localStorage if available
          try {
            const storedChats = JSON.parse(localStorage.getItem('userChats') || '[]');
            const chatIndex = storedChats.findIndex(c => c.id === chatData.id);
            if (chatIndex !== -1) {
              storedChats[chatIndex] = updatedChatData;
              localStorage.setItem('userChats', JSON.stringify(storedChats));
            }
          } catch (e) {
            console.log('Could not update localStorage chat data');
          }
        }
      } else {
        console.log('❌ Client profile not found in users collection, trying fallback sources...');
        
        // FALLBACK: Try to get client info from chat data or case data
        let fallbackName = 'Client';
        let fallbackProfilePic = '/default-avatar.svg';
        
        if (chatData?.clientName) {
          fallbackName = chatData.clientName;
          console.log('✅ Using client name from chat data:', fallbackName);
        } else if (caseData?.clientName) {
          fallbackName = caseData.clientName;
          console.log('✅ Using client name from case data:', fallbackName);
        }
        
        if (chatData?.clientPhoto) {
          fallbackProfilePic = chatData.clientPhoto;
          console.log('✅ Using client photo from chat data:', fallbackProfilePic);
        } else if (caseData?.clientPhoto) {
          fallbackProfilePic = caseData.clientPhoto;
          console.log('✅ Using client photo from case data:', fallbackProfilePic);
        }
        
        const fallbackProfileData = {
          id: clientId,
          fullName: fallbackName,
          profilePic: fallbackProfilePic,
          phone: 'Not available',
          email: 'Not available'
        };
        
        console.log('✅ Setting fallback client info:', fallbackProfileData);
        setClientInfo(fallbackProfileData);
        setClientProfile(fallbackProfileData);
      }
    } catch (error) {
      console.error('❌ Error loading client profile:', error);
      
      // ERROR FALLBACK: Try to get client info from chat data or case data
      let fallbackName = 'Client';
      let fallbackProfilePic = '/default-avatar.svg';
      
      if (chatData?.clientName) {
        fallbackName = chatData.clientName;
        console.log('✅ Using client name from chat data (error fallback):', fallbackName);
      } else if (caseData?.clientName) {
        fallbackName = caseData.clientName;
        console.log('✅ Using client name from case data (error fallback):', fallbackName);
      }
      
      if (chatData?.clientPhoto) {
        fallbackProfilePic = chatData.clientPhoto;
        console.log('✅ Using client photo from chat data (error fallback):', fallbackProfilePic);
      } else if (caseData?.clientPhoto) {
        fallbackProfilePic = caseData.clientPhoto;
        console.log('✅ Using client photo from case data (error fallback):', fallbackProfilePic);
      }
      
      const fallbackProfileData = {
        id: clientId,
        fullName: fallbackName,
        profilePic: fallbackProfilePic,
        phone: 'Not available',
        email: 'Not available'
      };
      
      console.log('✅ Setting fallback client info (error fallback):', fallbackProfileData);
      setClientInfo(fallbackProfileData);
      setClientProfile(fallbackProfileData);
    }
  };

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle typing indicator
  const handleTyping = useCallback(() => {
    if (!caseId) return;
    
    // Update local typing state
    setIsTyping(true);
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    const timeout = setTimeout(() => {
      setIsTyping(false);
    }, 3000);
    setTypingTimeout(timeout);

    // Send typing status to Firebase for real-time updates
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      if (currentUser?.id) {
        chatService.updateTypingStatus(actualChatId, currentUser.id, true);
        
        // Clear typing status after 3 seconds
        setTimeout(() => {
          chatService.updateTypingStatus(actualChatId, currentUser.id, false);
        }, 3000);
      }
    } catch (error) {
      console.error('❌ Error updating typing status:', error);
    }

    console.log('⌨️ Typing indicator active');
  }, [typingTimeout, actualChatId]);

  // Send text message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending || !caseId) return;
    
    // Prevent sending messages in completed consultations
    if (consultationCompleted) {
      toast.error('Cannot send messages in completed consultations');
      return;
    }

    const messageText = newMessage.trim();
    setNewMessage('');
    setSending(true);

    try {
      const messageData = {
        content: messageText,
        sender: 'lawyer',
        type: 'text',
        timestamp: new Date().toISOString()
      };

      console.log('📤 Sending message:', messageData);
      
      // Send message using chat service
      const result = await chatService.sendMessage(actualChatId, messageData);
      
      if (result.success) {
        console.log('✅ Message sent successfully');
        toast.success('Message sent!');
        
        // Message will be added to state via real-time subscription
      } else {
        console.error('❌ Failed to send message:', result.error);
        toast.error(result.error || 'Failed to send message');
        setNewMessage(messageText); // Restore message
      }
      
    } catch (error) {
      console.error('❌ Error sending message:', error);
      toast.error('Failed to send message: ' + error.message);
      setNewMessage(messageText); // Restore message
    } finally {
      setSending(false);
    }
  };

  // Handle file selection
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // If it's an image, show preview first
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview({
          file: file,
          url: e.target.result,
          name: file.name
        });
        setShowImagePreview(true);
      };
      reader.readAsDataURL(file);
      return;
    }

    // For non-image files, send directly
    await sendFile(file);
  };

  // Send file after preview (for images) or directly (for other files)
  const sendFile = async (file) => {
    // Prevent sending files in completed consultations
    if (consultationCompleted) {
      toast.error('Cannot send files in completed consultations');
      return;
    }
    
    // Show loading toast
    const loadingToast = toast.loading('Uploading file...');

    // Validate file
    const validation = validateFileForUpload(file, {
      maxSizeMB: 25, // Increased to 25MB for better support
      allowedTypes: [
        'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml',
        'video/mp4', 'video/avi', 'video/mov', 'video/webm', 'video/mkv', 'video/flv',
        'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a', 'audio/aac', 'audio/flac',
        'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/zip', 'application/rar', 'application/x-rar-compressed'
      ]
    });

    if (!validation.isValid) {
      toast.dismiss(loadingToast);
      toast.error(validation.errors[0]);
      return;
    }

    setSending(true);
    try {
      // Determine file type for better message display
      let fileType = 'file';
      if (file.type.startsWith('image/')) fileType = 'image';
      else if (file.type.startsWith('video/')) fileType = 'video';
      else if (file.type.startsWith('audio/')) fileType = 'audio';
      else if (file.type.includes('pdf') || file.type.includes('document')) fileType = 'document';

      const messageData = {
        content: file.name,
        sender: 'lawyer',
        type: fileType,
        fileSize: formatFileSize(file.size),
        fileType: file.type,
        timestamp: new Date().toISOString()
      };

      const result = await chatService.sendMessage(actualChatId, messageData, file);
      
      if (result.success) {
        console.log('✅ File sent successfully');
        toast.dismiss(loadingToast);
        toast.success(`${fileType.charAt(0).toUpperCase() + fileType.slice(1)} sent successfully`);
        
        // Message will be added to state via real-time subscription
      } else {
        console.error('❌ Failed to send file:', result.error);
        toast.dismiss(loadingToast);
        toast.error('Failed to send file');
      }
    } catch (error) {
      console.error('❌ Error sending file:', error);
      toast.dismiss(loadingToast);
      toast.error('Failed to send file');
    } finally {
      setSending(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle voice recording
  const handleVoiceRecordingComplete = async (audioBlob) => {
    // Prevent sending voice messages in completed consultations
    if (consultationCompleted) {
      toast.error('Cannot send voice messages in completed consultations');
      setShowVoiceRecorder(false);
      return;
    }
    
    setShowVoiceRecorder(false);
    
    // Show loading toast
    const loadingToast = toast.loading('Sending voice message...');
    setSending(true);

    try {
      // Create a file from the blob
      const audioFile = new File([audioBlob], `voice_message_${Date.now()}.webm`, {
        type: 'audio/webm'
      });

      const messageData = {
        content: 'Voice message',
        sender: 'lawyer',
        type: 'voice',
        fileSize: formatFileSize(audioBlob.size),
        fileType: 'audio/webm',
        timestamp: new Date().toISOString()
      };

      const result = await chatService.sendMessage(actualChatId, messageData, audioFile);
      
      if (result.success) {
        console.log('✅ Voice message sent successfully');
        toast.dismiss(loadingToast);
        toast.success('Voice message sent');
        
        // Message will be added to state via real-time subscription
      } else {
        console.error('❌ Failed to send voice message:', result.error);
        toast.dismiss(loadingToast);
        toast.error('Failed to send voice message');
      }
    } catch (error) {
      console.error('❌ Error sending voice message:', error);
      toast.dismiss(loadingToast);
      toast.error('Failed to send voice message');
    } finally {
      setSending(false);
    }
  };

  // Handle media preview
  const handleMediaClick = (media) => {
    setSelectedMedia(media);
    setShowMediaPreview(true);
  };

  // Handle file download
  const handleFileDownload = async (url, filename) => {
    let loadingToast = null;
    try {
      // Show loading toast
      loadingToast = toast.loading('Downloading file...');
      
      // Try multiple approaches for downloading
      let success = false;
      
      // Method 1: Try with no-cors mode first
      try {
        const response = await fetch(url, {
          method: 'GET',
          mode: 'no-cors',
        });
        
        if (response.type === 'opaque') {
          // For no-cors, we can't read the response, so try direct download
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
          link.style.display = 'none';
          
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
          
          success = true;
        }
      } catch (noCorsError) {
        console.log('No-cors method failed, trying other approaches...');
      }
      
      // Method 2: Try with XMLHttpRequest for better CORS handling
      if (!success) {
        try {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', url, true);
          xhr.responseType = 'blob';
          xhr.withCredentials = false;
          
          xhr.onload = function() {
            if (xhr.status === 200) {
              const blob = xhr.response;
              const blobUrl = window.URL.createObjectURL(blob);
              
              const link = document.createElement('a');
              link.href = blobUrl;
              link.download = filename;
              link.style.display = 'none';
              
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              
              setTimeout(() => {
                window.URL.revokeObjectURL(blobUrl);
              }, 1000);
              
              toast.dismiss(loadingToast);
              toast.success(`${filename} downloaded successfully`);
            } else {
              toast.dismiss(loadingToast);
              toast.error(`Download failed: ${xhr.status}`);
            }
          };
          
          xhr.onerror = function() {
            toast.dismiss(loadingToast);
            toast.error('Network error during download');
          };
          
          xhr.send();
          success = true;
        } catch (xhrError) {
          console.log('XMLHttpRequest method failed:', xhrError);
        }
      }
      
      // Method 3: Fallback to direct link (works in some browsers)
      if (!success) {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.dismiss(loadingToast);
        toast.success(`${filename} download initiated`);
      }
      
    } catch (error) {
      console.error('❌ Error downloading file:', error);
      if (loadingToast) {
        toast.dismiss(loadingToast);
      }
      toast.error('Failed to download file. Please try again.');
    }
  };

  // Handle media download from MediaPreviewModal
  const handleMediaDownload = (media) => {
    const filename = media.name || 'download';
    handleFileDownload(media.url, filename);
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle call
  const handleCall = () => {
    toast.info('Call feature coming soon!');
  };

  // Handle video call
  const handleVideoCall = () => {
    toast.info('Video call feature coming soon!');
  };

  // Handle profile view
  const handleProfileView = () => {
    setShowClientProfile(true);
  };

  // Handle delete chat
  const handleDeleteChat = async () => {
    if (!actualChatId) return;

    try {
      const { deleteDoc } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');

      await deleteDoc(doc(db, 'chats', actualChatId));
      console.log('✅ Chat deleted:', actualChatId);
      toast.success('Chat deleted successfully!');
      navigate('/lawyer/chat-history'); // Redirect to chat history
    } catch (error) {
      console.error('❌ Error deleting chat:', error);
      toast.error('Failed to delete chat. Please try again.');
    }
  };


  return (
            <div className="flex flex-col h-screen bg-gray-50 overflow-hidden relative pb-20" key={`chat-${caseId}-${clientInfo?.fullName || 'loading'}`}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          
          <div className="flex items-center space-x-3">
            {!clientInfo ? (
              // Loading state
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900">Loading...</h2>
                  <div className="text-xs text-gray-500">
                    Case ID: {caseId}
                  </div>
                </div>
              </div>
            ) : (
              // Client info display
              <>
            {clientInfo.profilePic && clientInfo.profilePic !== '/default-avatar.svg' ? (
              <img
                src={clientInfo.profilePic}
                    alt={clientInfo.fullName}
                    className="w-10 h-10 rounded-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={handleViewClientProfile}
                onError={(e) => {
                  console.log('❌ Image failed to load:', clientInfo.profilePic);
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
                onLoad={() => {
                  console.log('✅ Image loaded successfully:', clientInfo.profilePic);
                }}
              />
            ) : null}
                <div 
                  className={`w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity ${clientInfo.profilePic && clientInfo.profilePic !== '/default-avatar.svg' ? 'hidden' : ''}`}
                  onClick={handleViewClientProfile}
                >
              <span className="text-white font-semibold text-sm">
                    {clientInfo.fullName ? clientInfo.fullName.charAt(0).toUpperCase() : 'C'}
              </span>
            </div>
                <div className="cursor-pointer" onClick={handleViewClientProfile}>
              <div className="flex items-center space-x-2">
                    <h2 className="font-semibold text-gray-900 hover:text-purple-600 transition-colors">
                      {clientInfo.fullName}
                </h2>
                {isTyping && (
                  <div className="flex items-center space-x-1">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                    <span className="text-sm text-gray-500">typing...</span>
                  </div>
                )}
              </div>
              
              {/* Booking Details Display */}
              {(chatData?.bookingDate || chatData?.bookingTime || chatData?.consultationType) && (
                <div className="mt-1 text-xs text-gray-500 space-y-1">
                  {chatData.consultationType && (
                    <div className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-purple-400 rounded-full"></span>
                      <span>{chatData.consultationType}</span>
                    </div>
                  )}
                  {chatData.bookingDate && (
                    <div className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                      <span>Booked: {new Date(chatData.bookingDate).toLocaleDateString()}</span>
                    </div>
                  )}
                  {chatData.bookingTime && (
                    <div className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                      <span>Time: {chatData.bookingTime}</span>
                    </div>
                  )}
                </div>
              )}
              </div>
              </>
            )}
          </div>
        </div>

                 {/* Action Buttons */}
         <div className="flex items-center space-x-2">
           <button
             onClick={handleCall}
             className="p-2 bg-lavender-100 hover:bg-lavender-200 rounded-full transition-colors"
             title="Call"
           >
             <Phone className="w-5 h-5 text-purple-600" />
           </button>
           
           <button
             onClick={handleVideoCall}
             className="p-2 bg-lavender-100 hover:bg-lavender-200 rounded-full transition-colors"
             title="Video Call"
           >
             <Video className="w-5 h-5 text-purple-600" />
           </button>
           
           {/* Delete Chat Button */}
           <button
             onClick={handleDeleteChat}
             className="p-2 bg-red-100 hover:bg-red-200 rounded-full transition-colors"
             title="Delete Chat"
           >
             <Trash2 className="w-5 h-5 text-red-600" />
           </button>

         </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-40">
                 {loading ? (
           <div className="flex items-center justify-center h-full">
             <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
           </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-lg font-medium">No messages yet</p>
              <p className="text-sm">Start the conversation with your client</p>
            </div>
          </div>
        ) : (
          messages.map((message, index) => (
            <MessageBubble
              key={message.id || index}
              message={message}
              isOwnMessage={message.sender === 'lawyer' || message.senderId === 'lawyer'}
              onMediaClick={handleMediaClick}
              onDownload={handleFileDownload}
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="fixed bottom-20 left-0 right-0 bg-white border-t border-gray-200 p-3 z-10 max-w-md mx-auto" style={{ zIndex: 1000, position: 'fixed' }}>
        {showVoiceRecorder ? (
          <VoiceRecorder
            onRecordingComplete={handleVoiceRecordingComplete}
            onCancel={() => setShowVoiceRecorder(false)}
          />
        ) : (
          <div className="flex items-end gap-3 w-full">
            <button
              onClick={() => setShowAttachmentMenu(!showAttachmentMenu)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
            >
              <Paperclip className="w-5 h-5 text-gray-600" />
            </button>
            
            <div className="flex-1 min-w-0">
              <textarea
                ref={textareaRef}
                value={newMessage}
                onChange={(e) => {
                  setNewMessage(e.target.value);
                  handleTyping();
                }}
                onKeyPress={handleKeyPress}
                placeholder="Type a message..."
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none bg-white"
                rows="1"
                style={{ minHeight: '52px', maxHeight: '120px' }}
              />
            </div>
            
            <button
              onClick={() => setShowVoiceRecorder(true)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
              title="Voice Message"
            >
              <Mic className="w-5 h-5 text-gray-600" />
            </button>
            
            <button
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || sending}
              className="p-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              title="Send Message"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        )}
        
        {/* Enhanced Attachment Menu */}
        {showAttachmentMenu && (
          <div className="mt-3 p-4 bg-white border border-gray-200 rounded-xl shadow-lg">
            <div className="mb-3">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Send Files</h4>
              <p className="text-xs text-gray-500">Choose what you want to send</p>
            </div>
            
            <div className="grid grid-cols-4 gap-3">
              {/* Photo */}
              <button
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'image/*';
                  input.multiple = true;
                  input.onchange = (e) => {
                    Array.from(e.target.files).forEach(file => {
                      const event = { target: { files: [file] } };
                      handleFileSelect(event);
                    });
                  };
                  input.click();
                  setShowAttachmentMenu(false);
                }}
                className="flex flex-col items-center gap-2 p-3 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors border border-blue-200"
              >
                <Image className="w-6 h-6 text-blue-600" />
                <span className="text-xs font-medium text-blue-700">Photo</span>
              </button>
              
              {/* Video */}
              <button
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'video/*';
                  input.onchange = (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const event = { target: { files: [file] } };
                      handleFileSelect(event);
                    }
                  };
                  input.click();
                  setShowAttachmentMenu(false);
                }}
                className="flex flex-col items-center gap-2 p-3 rounded-lg bg-green-50 hover:bg-green-100 transition-colors border border-green-200"
              >
                <Video className="w-6 h-6 text-green-600" />
                <span className="text-xs font-medium text-green-700">Video</span>
              </button>
              
              {/* Document */}
              <button
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = '.pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,.zip,.rar';
                  input.onchange = (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const event = { target: { files: [file] } };
                      handleFileSelect(event);
                    }
                  };
                  input.click();
                  setShowAttachmentMenu(false);
                }}
                className="flex flex-col items-center gap-2 p-3 rounded-lg bg-purple-50 hover:bg-purple-100 transition-colors border border-purple-200"
              >
                <FileText className="w-6 h-6 text-purple-600" />
                <span className="text-xs font-medium text-purple-700">Document</span>
              </button>
              
              {/* Audio */}
              <button
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'audio/*';
                  input.onchange = (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const event = { target: { files: [file] } };
                      handleFileSelect(event);
                    }
                  };
                  input.click();
                  setShowAttachmentMenu(false);
                }}
                className="flex flex-col items-center gap-2 p-3 rounded-lg bg-orange-50 hover:bg-orange-100 transition-colors border border-orange-200"
              >
                <Music className="w-6 h-6 text-orange-600" />
                <span className="text-xs font-medium text-orange-700">Audio</span>
              </button>
            </div>
            
            {/* Quick Actions */}
            <div className="mt-4 pt-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Quick Actions</span>
                <button
                  onClick={() => setShowAttachmentMenu(false)}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          className="hidden"
          accept="image/*"
        />
      </div>

      {/* Client Profile Modal */}
      {showClientProfile && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Client Profile</h2>
              <button
                onClick={() => setShowClientProfile(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>
            
            {/* Profile Content */}
            <div className="p-6">
              {/* Basic Info */}
              <div className="flex items-start gap-6 mb-8">
                <img
                  src={selectedClient.profilePic}
                  alt={selectedClient.name}
                  className="w-24 h-24 rounded-full object-cover border-4 border-purple-100"
                />
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{selectedClient.name}</h3>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{selectedClient.city}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bio */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">About</h4>
                <p className="text-gray-600 leading-relaxed">{selectedClient.bio}</p>
              </div>

              {/* Booking Details */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Booking Details</h4>
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-4">
                <div>
                        <h5 className="font-semibold text-gray-900 mb-2">Consultation Information</h5>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Type:</span>
                            <span className="font-medium text-gray-900">{selectedClient.bookingDetails?.consultationType || 'Not specified'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Case Type:</span>
                            <span className="font-medium text-gray-900">{selectedClient.bookingDetails?.caseType || 'Not specified'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Status:</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              selectedClient.bookingDetails?.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                              selectedClient.bookingDetails?.status === 'active' ? 'bg-green-100 text-green-700' :
                              selectedClient.bookingDetails?.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {selectedClient.bookingDetails?.status?.toUpperCase() || 'UNKNOWN'}
                            </span>
                          </div>
                          {selectedClient.bookingDetails?.isEmergency && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Priority:</span>
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                EMERGENCY
                              </span>
                            </div>
                          )}
                </div>
              </div>
              
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-2">Schedule</h5>
              <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Date:</span>
                            <span className="font-medium text-gray-900">
                              {selectedClient.bookingDetails?.appointmentDate || 'Not specified'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Time:</span>
                            <span className="font-medium text-gray-900">{selectedClient.bookingDetails?.appointmentTime || 'Not specified'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Booked On:</span>
                            <span className="font-medium text-gray-900">
                              {formatTimestamp(selectedClient.bookingDetails?.createdAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                </div>
                
                    {/* Right Column */}
                    <div className="space-y-4">
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-2">Pricing</h5>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Consultation Fees:</span>
                            <span className="font-medium text-gray-900">₹{selectedClient.bookingDetails?.consultationFees || 0}</span>
                </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Service Charge:</span>
                            <span className="font-medium text-gray-900">₹{selectedClient.bookingDetails?.serviceCharge || 0}</span>
              </div>
                          <div className="flex justify-between border-t pt-2">
                            <span className="text-gray-900 font-semibold">Total Amount:</span>
                            <span className="text-gray-900 font-bold text-lg">₹{selectedClient.bookingDetails?.totalAmount || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Your Earnings (80%):</span>
                            <span className="font-medium text-green-600">₹{((selectedClient.bookingDetails?.totalAmount || 0) * 0.8).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-2">Case Description</h5>
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <p className="text-gray-700 text-sm leading-relaxed">
                            {selectedClient.bookingDetails?.description || 'No description provided by the client.'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Preferences */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Client Preferences</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="font-medium text-gray-900 mb-2">Consultation Mode</div>
                    <div className="text-gray-600">
                      {selectedClient.preferences?.consultationMode || 'Not specified'}
                    </div>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="font-medium text-gray-900 mb-2">Preferred Time</div>
                    <div className="text-gray-600">{selectedClient.preferences?.preferredTime || 'Not specified'}</div>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="font-medium text-gray-900 mb-2">Language</div>
                    <div className="text-gray-600">{selectedClient.preferences?.language || 'Not specified'}</div>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="font-medium text-gray-900 mb-2">Special Requirements</div>
                    <div className="text-gray-600">{selectedClient.preferences?.specialRequirements || 'None'}</div>
                  </div>
                </div>
              </div>

              {/* Close Button */}
              <div className="mt-6 flex justify-center">
                <button
                  onClick={() => setShowClientProfile(false)}
                  className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Media Preview Modal */}
      <MediaPreviewModal
        isOpen={showMediaPreview}
        onClose={() => setShowMediaPreview(false)}
        media={selectedMedia}
          onDownload={handleMediaDownload}
        />

      {/* Image Preview Modal (Before Sending) */}
      {showImagePreview && imagePreview && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-sm w-full max-h-[60vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b">
              <h3 className="text-base font-semibold text-gray-900">Preview Image</h3>
              <button
                onClick={() => {
                  setShowImagePreview(false);
                  setImagePreview(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Image Preview */}
            <div className="p-3">
              <img
                src={imagePreview.url}
                alt="Preview"
                className="w-full h-48 object-cover rounded-lg mb-3"
              />
              <p className="text-xs text-gray-600 mb-3">{imagePreview.name}</p>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-2 p-3 border-t">
              <button
                onClick={() => {
                  setShowImagePreview(false);
                  setImagePreview(null);
                }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setShowImagePreview(false);
                  await sendFile(imagePreview.file);
                  setImagePreview(null);
                }}
                disabled={sending}
                className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 text-sm"
              >
                {sending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LawyerChatConversation;

